// @ts-nocheck
import React, { useCallback, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Checkbox,
  Link,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';

import { CHECKLIST_TYPES } from '../../../../const/CommonConst';
import { isValueEmpty } from '../../../../utils/Utils';
import RenderCheckListAttribute from '../ChecklistAttribute/RenderCheckListAttribute';
import useDashboard from '../../../../hooks/ModelInventory/useDashboard';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';

const DEFAULT_CHECKLIST_LINK_LABEL = 'View Checklist';

/**
 * ChecklistModal component for displaying checklist details in a modal dialog
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.handleClose - Function to close the modal
 * @param {Object} props.value - The checklist value object
 * @param {Object} props.attribute - The attribute object with metadata
 * @returns {React.ReactElement} The checklist modal component
 */
const ChecklistModal = ({
  open,
  handleClose,
  value,
  attribute,
  entityId = null,
  entityType = null,
}) => {
  const { getAllSavedSearches } = useDashboard();
  const { getAllModelEntity } = useModelInventory();

  const handleCloseModal = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const saveSearch = attribute?.checklist_type === CHECKLIST_TYPES.SAVE_SEARCH;

  /**
   * Renders individual checkbox for normal checklist items in read mode
   * @function
   * @param {string} item - The checklist item
   * @param {boolean} checked - Whether the item is checked
   * @returns {React.ReactElement} The checkbox component
   */
  const renderCheckboxItem = useCallback((item, checked) => {
    return (
      <Box key={item} display="flex" alignItems="center" mb={1}>
        <Checkbox
          checked={checked}
          disabled
          sx={{ padding: 0, mr: 1 }}
          size="small"
        />
        <Typography variant="body2">{item}</Typography>
      </Box>
    );
  }, []);

  // Mock formik form object for RenderCheckListAttribute compatibility in read mode
  const mockFormikForm = {
    values: {
      [attribute?.id]: value,
    },
    setFieldValue: useCallback(() => {
      // No-op for read mode
    }, []),
  };

  // Mock section object for RenderCheckListAttribute compatibility
  const mockSection = {
    section_name: attribute?.section_name || 'defaultSection',
  };

  /**
   * Renders the content for savedSearch type checklist
   * @returns {React.ReactElement} The savedSearch content
   */
  const renderSavedSearchContent = useCallback(() => {
    // For savedSearch type, use the existing RenderCheckListAttribute component in view mode
    return (
      <Box>
        <RenderCheckListAttribute
          attribute={attribute}
          formikForm={mockFormikForm}
          mode="view"
          getAllSavedSearches={getAllSavedSearches}
          getAllModelEntity={getAllModelEntity}
          entityId={entityId}
          entity_type={entityType}
        />
      </Box>
    );
  }, [
    attribute,
    mockFormikForm,
    mockSection,
    getAllSavedSearches,
    getAllModelEntity,
  ]);

  /**
   * Renders the content for normal type checklist
   * @returns {React.ReactElement} The normal checklist content
   */
  const renderNormalContent = useCallback(() => {
    const { checkedEntity } = value;

    if (!checkedEntity) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary">
            No checklist items available
          </Typography>
        </Box>
      );
    }

    if (
      typeof checkedEntity === 'object' &&
      Object.keys(checkedEntity).length > 0
    ) {
      // Render checkboxes when items exist (adapted from ChecklistFieldComponent)
      return (
        <Box>
          {Object.entries(checkedEntity || {}).map(([item, checked]) =>
            renderCheckboxItem(item, checked)
          )}
        </Box>
      );
    }

    // For default values which are in string format; guard against non-string types
    const defaultItems = {};
    if (typeof checkedEntity === 'string') {
      checkedEntity
        .trim()
        .split(',')
        .forEach((option) => {
          const trimmedOption = option.trim();
          if (
            !isValueEmpty(trimmedOption) &&
            !(trimmedOption in defaultItems)
          ) {
            defaultItems[trimmedOption] = false; // Initially unchecked
          }
        });
    }

    return (
      <Box>
        {Object.entries(defaultItems || {}).map(([item, checked]) =>
          renderCheckboxItem(item, checked)
        )}
      </Box>
    );
  }, [value, renderCheckboxItem]);

  const filterName = value?.filterName?.trim?.();
  const fallbackTitle = saveSearch
    ? 'Saved Search Checklist Details'
    : 'Checklist Details';
  const modalTitle = isValueEmpty(filterName) ? fallbackTitle : filterName;

  return (
    <Dialog maxWidth="md" open={open} onClose={handleClose} fullWidth>
      {/* Right padding reserves space for the absolutely positioned close button */}
      <DialogTitle sx={{ pr: 8 }}>
        <Box
          component="span"
          title={modalTitle}
          aria-live="polite"
          aria-atomic="true"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {modalTitle}
        </Box>
      </DialogTitle>

      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleCloseModal}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={saveSearch ? { py: 0 } : {}}>
        {saveSearch ? renderSavedSearchContent() : renderNormalContent()}
      </DialogContent>
    </Dialog>
  );
};

ChecklistModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  value: PropTypes.shape({
    type: PropTypes.string,
    checkedEntity: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    entityType: PropTypes.string,
    filterName: PropTypes.string,
    userOfFilter: PropTypes.string,
  }).isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    section_name: PropTypes.string,
    checklist_type: PropTypes.string,
  }),
  entityId: PropTypes.string,
  entityType: PropTypes.string,
};

/**
 * ViewChecklist component for displaying checklist values in read mode
 * @param {Object} props - Component props
 * @param {Object} props.value - The checklist value object
 * @param {Object} props.attribute - The attribute object with metadata
 * @returns {React.ReactElement} The view checklist component
 */
const ViewChecklist = ({ value, attribute, entityId, entityType }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // Don't render if value is empty or invalid
  if (isValueEmpty(value) || !value?.type) {
    return '-';
  }

  const filterName = value?.filterName?.trim?.();
  const hasFilterName = !isValueEmpty(filterName);
  const linkLabel = hasFilterName ? filterName : DEFAULT_CHECKLIST_LINK_LABEL;

  return (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          maxWidth: '100%',
          minWidth: 0,
          verticalAlign: 'middle',
        }}
      >
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-label={`${linkLabel}, opens in dialog`}
          sx={{ minWidth: 0, overflow: 'hidden' }}
        >
          <Box
            component="span"
            title={hasFilterName ? filterName : ''}
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {linkLabel}
          </Box>
        </Link>
        <IconButton
          size="small"
          aria-label={`Preview ${linkLabel}`}
          title="Preview Checklist"
          onClick={handleOpen}
          sx={{ flexShrink: 0 }}
        >
          <OpenInNewIcon
            width={18}
            height={18}
            style={{ color: theme?.palette?.primary?.main }}
          />
        </IconButton>
      </Box>

      <ChecklistModal
        open={open}
        handleClose={handleClose}
        value={value}
        attribute={attribute}
        entityId={entityId}
        entityType={entityType}
      />
    </>
  );
};

ViewChecklist.propTypes = {
  value: PropTypes.shape({
    type: PropTypes.string,
    checkedEntity: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    entityType: PropTypes.string,
    filterName: PropTypes.string,
    userOfFilter: PropTypes.string,
  }).isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    section_name: PropTypes.string,
    checklist_type: PropTypes.string,
  }),
  entityId: PropTypes.string,
  entityType: PropTypes.string,
};

export default ViewChecklist;
